import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icon from './AppIcon';
import Button from './ui/Button';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);

  const volunteerNavItems = [
    { path: '/mission-control-dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/smart-opportunity-finder', label: 'Find Opportunities', icon: 'Search' },
    { path: '/volunteer-journey-tracker', label: 'My Journey', icon: 'TrendingUp' },
    { path: '/event-experience-hub', label: 'Events', icon: 'Calendar' },
  ];

  const adminNavItems = [
    { path: '/admin-portal', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/admin/create-event', label: 'Create Event', icon: 'PlusCircle' },
    { path: '/admin/manage-volunteers', label: 'Volunteers', icon: 'Users' },
  ];

  const primaryNavItems = user?.role === 'admin' ? adminNavItems : volunteerNavItems;
  const secondaryNavItems = user?.role === 'admin' ? [] : [
    { path: '/organization-impact-profiles', label: 'Organizations', icon: 'Building2' },
    { path: '/skills-development-academy', label: 'Skills Academy', icon: 'GraduationCap' },
  ];

  const isActivePath = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsMoreMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef?.current && !moreMenuRef?.current?.contains(event?.target)) {
        setIsMoreMenuOpen(false);
      }
    };

    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="header">
        <div className="header-container">
          <div className="flex items-center gap-8">
            <button
              onClick={() => {
                if (user?.role === 'admin') {
                  handleNavigation('/admin-portal')
                } else {
                  handleNavigation('/mission-control-dashboard')
                }
              }}
              className="header-logo"
              aria-label="VolunteerHub Home"
            >
              <div className="header-logo-icon">
                <Icon name="Heart" size={20} color="white" />
              </div>
              <span className="hidden sm:inline">VolunteerHub</span>
            </button>

            <nav className="header-nav">
              {primaryNavItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`header-nav-link ${isActivePath(item?.path) ? 'active' : ''}`}
                >
                  {item?.label}
                </button>
              ))}

              <div className="relative" ref={moreMenuRef}>
                <button
                  onClick={toggleMoreMenu}
                  className="header-nav-link flex items-center gap-1"
                  aria-expanded={isMoreMenuOpen}
                  aria-haspopup="true"
                >
                  More
                  <Icon
                    name={isMoreMenuOpen ? 'ChevronUp' : 'ChevronDown'}
                    size={16}
                  />
                </button>

                {isMoreMenuOpen && (
                  <div className="dropdown-menu">
                    {secondaryNavItems?.map((item) => (
                      <button
                        key={item?.path}
                        onClick={() => handleNavigation(item?.path)}
                        className="dropdown-menu-item"
                      >
                        <Icon name={item?.icon} size={18} />
                        <span>{item?.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div className="header-actions">
            {user ? (
              <div className="flex items-center gap-4">
                <div className="hidden md:block text-right">
                  <p className="text-sm font-medium leading-none">{user.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                >
                  Logout
                </Button>
              </div>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="hidden lg:flex"
                onClick={() => handleNavigation('/login')}
              >
                Get Started
              </Button>
            )}

            <button
              onClick={toggleMobileMenu}
              className="header-mobile-toggle"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
      <div className={`mobile-menu ${!isMobileMenuOpen ? 'closed' : ''}`}>
        <div className="mobile-menu-header">
          <button
            onClick={() => {
              if (user?.role === 'admin') {
                handleNavigation('/admin-portal')
              } else {
                handleNavigation('/mission-control-dashboard')
              }
            }}
            className="header-logo"
          >
            <div className="header-logo-icon">
              <Icon name="Heart" size={20} color="white" />
            </div>
            <span>VolunteerHub</span>
          </button>

          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-md hover:bg-muted"
            aria-label="Close mobile menu"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <nav className="mobile-menu-nav">
          {[...primaryNavItems, ...secondaryNavItems]?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`mobile-menu-link flex items-center gap-3 ${isActivePath(item?.path) ? 'active' : ''
                }`}
            >
              <Icon name={item?.icon} size={20} />
              <span>{item?.label}</span>
            </button>
          ))}

          <div className="mt-4 pt-4 border-t border-border">
            {user ? (
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  logout();
                  toggleMobileMenu();
                }}
              >
                Logout
              </Button>
            ) : (
              <Button
                variant="default"
                fullWidth
                onClick={() => handleNavigation('/login')}
              >
                Get Started
              </Button>
            )}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;